import React from 'react';
import { Select, Typography, Tooltip, Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DropdownMenu, SparkIcon, TagGeneral as SparkTagGeneral, Text as SparkText, TooltipTrigger } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
const { Text } = Typography;
const { Option } = Select;

const SINGLE_LABEL_LIST_MODE_OPTIONS = [
    { value: 'first', label: 'First item in list' },
    { value: 'full', label: 'Entire list' },
];

const ClassificationSummary = ({
    taskType,
    isMultilabel,
    responseType,
    rawResponse,
    parsedJson,
    normalizedJsonLabelField,
    jsonLabelFieldOptions,
    setSelectedJsonLabelField,
    multilabelDelimiter,
    setMultilabelDelimiter,
    singleLabelListMode = 'first',
    setSingleLabelListMode = () => {},
    selectedJsonTokens,
    getJsonValueByPath,
    formatNerAssignedValue,
    splitLabels,
    delimiterOptions,
    getValuesByPath,
    initialParserSelections,
    parserOutput, // New prop for custom mode / validation results
    readOnly = false,
}) => {
    if (taskType !== TaskType.classification) {
        return null;
    }

    const renderLabelPills = (labels, emptyMessage) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {labels.length > 0 ? (
                labels.map((label, index) => (
                    <div key={`${label}-${index}`} className="response-step-card__selected-label-pill">
                        {label}
                    </div>
                ))
            ) : (
                <Text type="secondary">{emptyMessage}</Text>
            )}
        </div>
    );

    const classificationSummaryContainerStyle = {
        marginTop: 16,
        border: '1px solid #e5e5e5',
        borderRadius: 12,
        padding: 16,
        background: '#fff',
    };

    // Handle Parser Output Mode (Custom / Validation Result)
    if (parserOutput) {
        let labels = [];
        if (isMultilabel) {
            if (Array.isArray(parserOutput)) {
                labels = parserOutput.map(String);
            } else if (parserOutput && Array.isArray(parserOutput.labels)) {
                labels = parserOutput.labels.map(String);
            } else if (parserOutput && parserOutput.label) {
                labels = [String(parserOutput.label)];
            }
        } else {
            if (parserOutput && parserOutput.labels && Array.isArray(parserOutput.labels) && parserOutput.labels.length > 0) {
                labels = [String(parserOutput.labels[0])];
            } else if (parserOutput && parserOutput.label !== undefined) {
                labels = [String(parserOutput.label)];
            } else if (typeof parserOutput === 'string') {
                labels = [parserOutput];
            }
        }

        return (
            <div
                className="response-step-card__classification-summary"
                style={classificationSummaryContainerStyle}
            >
                <Text type="secondary">Extracted Labels:</Text>
                <div style={{ marginTop: 8 }}>
                    {renderLabelPills(labels, 'No labels found')}
                </div>
            </div>
        );
    }

    const renderDelimiterSelector = () => (
        <Select
            style={{ width: 140 }}
            value={delimiterOptions.find(option => option.value === multilabelDelimiter)?.value || 'custom'}
            disabled={readOnly}
            onChange={(value) => {
                if (value === 'custom') {
                    return;
                }
                setMultilabelDelimiter(value);
            }}
            dropdownRender={(menu) => (
                <>
                    {menu}
                    <div style={{ padding: 8 }}>
                        <Input
                            placeholder="Custom delimiter"
                            onPressEnter={(e) => {
                                setMultilabelDelimiter(e.target.value);
                            }}
                        />
                    </div>
                </>
            )}
        >
            {delimiterOptions.map(option => (
                <Option key={option.value} value={option.value}>
                    {option.label}
                </Option>
            ))}
        </Select>
    );

    const hasJsonSelection = Object.keys(selectedJsonTokens).length > 0;
    const selectedFieldValue = (responseType === 'json' && hasJsonSelection && normalizedJsonLabelField)
        ? getJsonValueByPath(parsedJson, normalizedJsonLabelField)
        : undefined;
    const selectedFieldDisplayValue = formatNerAssignedValue(selectedFieldValue);

    if (isMultilabel && responseType === 'json') {
        const effectiveFieldPath = normalizedJsonLabelField || initialParserSelections?.classification?.fieldPath || null;
        if (!effectiveFieldPath) {
            return null;
        }

        const labelValues = (parsedJson && effectiveFieldPath)
            ? getValuesByPath(parsedJson, effectiveFieldPath)
            : [];

        const selectedLabels = (labelValues && labelValues.length)
            ? labelValues.flatMap((val) => splitLabels(formatNerAssignedValue(val)))
            : splitLabels(selectedFieldDisplayValue);

        return (
            <div
                className="response-step-card__classification-summary"
                style={classificationSummaryContainerStyle}
            >
                <div className="classification-summary__row" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div className="classification-summary__field" style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary">Label Field</Text>
                        <Select
                            placeholder="Select field"
                            value={normalizedJsonLabelField || undefined}
                            onChange={setSelectedJsonLabelField}
                            options={jsonLabelFieldOptions}
                            style={{ width: 200, marginTop: 8 }}
                            disabled={readOnly || jsonLabelFieldOptions.length === 0}
                        />
                    </div>
                    <div className="classification-summary__field" style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary">Delimiter</Text>
                            <Tooltip title="Labels will be split using this delimiter">
                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                            </Tooltip>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {renderDelimiterSelector()}
                        </div>
                    </div>
                    <div className="classification-summary__field classification-summary__labels" style={{ flex: '2 1 260px', display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary">Selected labels:</Text>
                        <div style={{ marginTop: 8 }}>
                            {selectedLabels && selectedLabels.length > 0
                                ? renderLabelPills(selectedLabels, 'Highlight JSON values above to populate labels')
                                : <Text type="secondary">Labels will appear once this field contains values.</Text>}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (isMultilabel && responseType === 'text') {
        const labels = splitLabels(rawResponse || '');
        if (labels.length === 0) {
            return null;
        }
        return (
            <div
                className="response-step-card__classification-summary"
                style={classificationSummaryContainerStyle}
            >
                <div className="classification-summary__row" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div className="classification-summary__field" style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary">Delimiter</Text>
                            <Tooltip title="Labels will be split using this delimiter">
                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                            </Tooltip>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {renderDelimiterSelector()}
                        </div>
                    </div>
                    <div className="classification-summary__field classification-summary__labels" style={{ flex: '2 1 260px', display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary">Selected labels:</Text>
                        <div style={{ marginTop: 8 }}>
                            {renderLabelPills(labels, rawResponse ? 'No labels detected with current delimiter' : 'Run a sample to view labels')}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isMultilabel) {
        // List-valued field (e.g. Jev's {"labels": ["harmful"]}): the label is
        // either the list's first item (default) or the stringified list —
        // matching exactly what the generated parser returns.
        const isListField = responseType === 'json' && hasJsonSelection && Array.isArray(selectedFieldValue);
        const listDisplayValue = isListField
            ? (singleLabelListMode === 'first'
                ? (selectedFieldValue.length ? formatNerAssignedValue(selectedFieldValue[0]) : '')
                : formatNerAssignedValue(selectedFieldValue))
            : null;
        const labels = responseType === 'json'
            ? (
                hasJsonSelection && (isListField ? listDisplayValue : selectedFieldDisplayValue)
                    ? [isListField ? listDisplayValue : selectedFieldDisplayValue]
                    : []
            )
            : (rawResponse ? [rawResponse] : []);
        // Keep the card visible for an empty-list field so the mode selector
        // (and the empty message) still render.
        if ((!labels || labels.length === 0) && !isListField) {
            return null;
        }
        const mappedFieldLabel = responseType === 'json' && hasJsonSelection && normalizedJsonLabelField
            ? String(normalizedJsonLabelField)
            : null;
        return (
            <div
                className="response-step-card__classification-summary model-setup-map-summary"
                style={classificationSummaryContainerStyle}
            >
                {mappedFieldLabel ? (
                    <div className="model-setup-map-confirm fs-unmask" style={{ marginBottom: 8 }}>
                        <SparkText variant="detail-regular" color="primary" as="span">
                            Using
                        </SparkText>
                        <SparkTagGeneral label={mappedFieldLabel} />
                        <SparkText variant="detail-regular" color="primary" as="span">
                            as the predicted label.
                        </SparkText>
                    </div>
                ) : (
                    responseType === 'json' && !readOnly ? (
                        <SparkText variant="detail-regular" color="secondary" as="div" className="fs-unmask" style={{ marginBottom: 8 }}>
                            Click a value to map it as the prediction.
                        </SparkText>
                    ) : null
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <SparkText variant="base-medium" color="primary" className="fs-unmask">Extracted label</SparkText>
                    {isListField && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SparkText variant="base-regular" color="secondary">Use</SparkText>
                            <TooltipTrigger text="This field holds a list - choose whether the label is its first item or the entire list as text">
                                <span style={{ color: '#8c8c8c', display: 'inline-flex' }}>
                                    <SparkIcon slug="circle-info" size="small" />
                                </span>
                            </TooltipTrigger>
                            <div style={{ width: 170 }}>
                                <DropdownMenu
                                    size="small"
                                    showLabel={false}
                                    text={
                                        SINGLE_LABEL_LIST_MODE_OPTIONS.find(
                                            (opt) => opt.value === singleLabelListMode
                                        )?.label
                                    }
                                    items={SINGLE_LABEL_LIST_MODE_OPTIONS.map((opt) => ({
                                        label: opt.label,
                                        state: opt.value === singleLabelListMode ? 'selected' : 'default',
                                    }))}
                                    onSelect={(item, index) => {
                                        if (readOnly) return;
                                        const opt = SINGLE_LABEL_LIST_MODE_OPTIONS[index];
                                        if (opt) setSingleLabelListMode(opt.value);
                                    }}
                                    disabled={readOnly}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: 8 }}>
                    {renderLabelPills(labels, 'This field is an empty list')}
                </div>
            </div>
        );
    }

    return null;
};

export default ClassificationSummary;
